import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SteamIcon } from '@/components/icons/SteamIcon';
import { Shield, Lock, Zap, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ERROR_MESSAGES: Record<string, string> = {
  signature_invalid:
    'Steam отверг подпись (возможно, ссылка авторизации устарела или была использована повторно). ' +
    'Попробуйте войти ещё раз.',
  no_steam_id:
    'Steam не передал ваш идентификатор. Попробуйте войти ещё раз.',
  steam_api_failed:
    'Не удалось получить данные профиля из Steam Web API. Проверьте интернет-соединение.',
  db_failed:
    'Внутренняя ошибка сохранения профиля. Сообщите администратору.',
  server_error:
    'Внутренняя ошибка сервера при входе. Попробуйте ещё раз через минуту; ' +
    'если повторяется — сообщите администратору.',
  unknown:
    'Не удалось завершить вход через Steam.',
};

export function LoginPage() {
  const { isAuthenticated, login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Backend redirect'ит на /login?error=auth_failed&reason=<code>
  // если Steam OpenID flow упал.
  useEffect(() => {
    const error = searchParams.get('error');
    const reason = searchParams.get('reason') || 'unknown';
    if (error) {
      setErrorMsg(ERROR_MESSAGES[reason] || ERROR_MESSAGES.unknown);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-[#0d1117] flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="text-center">
          <div className="mx-auto w-20 h-20 relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-[#ff6b00] to-[#ff9500] rounded-xl transform rotate-3"></div>
            <div className="absolute inset-0 bg-[#0d1117] rounded-xl flex items-center justify-center border border-[#30363d]">
              <span className="text-[#ff6b00] font-black text-3xl">XP</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Добро пожаловать
          </h1>
          <p className="text-[#8b949e]">
            Войдите через Steam для доступа к маркетплейсу
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-3 gap-4 py-8">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-[#1a2332] rounded-lg flex items-center justify-center border border-[#30363d]">
              <Zap className="w-6 h-6 text-[#ff6b00]" />
            </div>
            <p className="text-xs text-[#8b949e]">Мгновенные сделки</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-[#1a2332] rounded-lg flex items-center justify-center border border-[#30363d]">
              <Shield className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-xs text-[#8b949e]">Безопасность</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-[#1a2332] rounded-lg flex items-center justify-center border border-[#30363d]">
              <Lock className="w-6 h-6 text-blue-500" />
            </div>
            <p className="text-xs text-[#8b949e]">Защита сделок</p>
          </div>
        </div>

        {/* Error banner — backend reason */}
        {errorMsg && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-red-400 font-medium mb-1">Ошибка авторизации</p>
                <p className="text-[#8b949e]">{errorMsg}</p>
              </div>
            </div>
          </div>
        )}

        {/* Login Button */}
        <div className="space-y-4">
          <Button
            onClick={login}
            disabled={isLoading}
            className="w-full h-14 bg-gradient-to-r from-[#1b2838] to-[#2a475e] hover:from-[#2a475e] hover:to-[#1b2838] text-white border border-[#66c0f4]/30 text-lg font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-[#66c0f4]/20"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <SteamIcon className="w-6 h-6 mr-3" />
                Войти через Steam
              </>
            )}
          </Button>

          <p className="text-center text-xs text-[#8b949e]">
            Авторизуясь, вы соглашаетесь с{' '}
            <Link to="/terms" className="text-[#66c0f4] hover:underline">
              условиями использования
            </Link>
          </p>
        </div>

        {/* Security Notice */}
        <div className="bg-[#1a2332] rounded-lg p-4 border border-[#30363d]">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#8b949e]">
              <p className="text-white font-medium mb-1">Безопасная авторизация</p>
              <p>Мы не храним ваш пароль Steam. Авторизация происходит через официальный OpenID сервис Steam.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
